import { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { HistoryEntry, OfficerMetadata, ShiftType } from '@/types/api';
import { useVersions } from '@/utils/versionStore';
import { getStartHour, formatCounterLabel, naturalSortOfficers } from '@/config/modeConfig';
import { BreakDurationBadge } from '@/components/BreakBattery';
import { UI_SCALE, popupMaxH } from '@/utils/uiScale';

type GridTrace = { row: number; col: number; color: string; traceType: 'add' | 'delete' | 'change' };

interface Props {
  /** Version headers; the two compared are fetched by id (see utils/versionStore). */
  history: HistoryEntry[];
  officerMetadata: Record<string, OfficerMetadata>;
  shiftType: ShiftType | undefined;
  operationMode: string | undefined;
  onClose: () => void;
  /**
   * Called with the cell-level diff between the two selected versions, so the
   * counter grid behind this modal can highlight it live — like Google Docs
   * showing an edit trail inline instead of only in a side-by-side list.
   * Called again whenever the selected pair changes, and with null on close/
   * unmount so the highlight doesn't outlive the comparison it came from.
   */
  onDiffTraces?: (traces: GridTrace[] | null) => void;
}

/** Cell-level diff between two counter_matrix snapshots, colored by what
 * happened to the cell: green = counter opened, red = counter vacated,
 * amber = a different officer took it over (mirrors the modal's own
 * added/deleted/changed legend for changedOfficerKeys). */
function computeMatrixDiffTraces(matrixA: string[][][], matrixB: string[][][]): GridTrace[] {
  const traces: GridTrace[] = [];
  const numRows = Math.max(matrixA.length, matrixB.length);
  for (let row = 0; row < numRows; row++) {
    const numCols = Math.max(matrixA[row]?.length ?? 0, matrixB[row]?.length ?? 0);
    for (let col = 0; col < numCols; col++) {
      const aOfficers = (matrixA[row]?.[col] ?? []).join(',');
      const bOfficers = (matrixB[row]?.[col] ?? []).join(',');
      if (aOfficers === bOfficers) continue;
      const wasEmpty = aOfficers === '';
      const isNowEmpty = bOfficers === '';
      const traceType: GridTrace['traceType'] = wasEmpty ? 'add' : isNowEmpty ? 'delete' : 'change';
      const color = traceType === 'add' ? '#16a34a' : traceType === 'delete' ? '#dc2626' : '#d97706';
      traces.push({ row, col, color, traceType });
    }
  }
  return traces;
}

interface Segment {
  value: number;
  startSlot: number;
  endSlot: number;
}

type BadgeType = 'addition' | 'mixed' | 'unchanged';

function slotToTime(slot: number, startHour: number): string {
  const totalMin = startHour * 60 + slot * 15;
  const h = Math.floor(totalMin / 60) % 24;
  const m = totalMin % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function mergeSegments(schedule: number[]): Segment[] {
  const segments: Segment[] = [];
  let i = 0;
  while (i < schedule.length) {
    const val = schedule[i] ?? 0;
    if (val === 0) { i++; continue; }
    let j = i + 1;
    while (j < schedule.length && (schedule[j] ?? 0) === val) j++;
    segments.push({ value: val, startSlot: i, endSlot: j });
    i = j;
  }
  return segments;
}

const normVal = (n: number) => n < 0 ? -1 : n;

// Badge for a segment in version B, relative to version A
function segBadgeForB(seg: Segment, schedA: number[]): BadgeType {
  const normSeg = normVal(seg.value);
  for (let slot = seg.startSlot; slot < seg.endSlot; slot++) {
    const av = schedA[slot] ?? 0;
    if (normVal(av) !== normSeg) return av === 0 ? 'addition' : 'mixed';
  }
  if (normVal(schedA[seg.startSlot - 1] ?? 0) === normSeg) return 'mixed';
  if (normVal(schedA[seg.endSlot] ?? 0) === normSeg) return 'mixed';
  return 'unchanged';
}

// Find the segment in segsA with the greatest slot overlap with seg.
// Returns null if no overlap exists (seg is a pure addition).
function findBestMatchInA(seg: Segment, segsA: Segment[]): Segment | null {
  let best: Segment | null = null;
  let bestOverlap = 0;
  for (const a of segsA) {
    const overlap = Math.max(0, Math.min(a.endSlot, seg.endSlot) - Math.max(a.startSlot, seg.startSlot));
    if (overlap > bestOverlap) { bestOverlap = overlap; best = a; }
  }
  return bestOverlap > 0 ? best : null;
}

function hasChanges(a: number[], b: number[]): boolean {
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i++) {
    if ((a[i] ?? 0) !== (b[i] ?? 0)) return true;
  }
  return false;
}

// Same order as the officer graph and every dropdown (one source of truth).
function sortOfficerKeys(keys: string[]): string[] {
  return naturalSortOfficers(keys);
}

const BADGE_STYLES: Record<BadgeType, { color: string; bg: string; label: string; border: string }> = {
  addition: { color: '#16a34a', bg: '#f0fdf4', label: 'new',     border: '#22c55e' },
  mixed:    { color: '#d97706', bg: '#fffbeb', label: 'changed',  border: '#f59e0b' },
  unchanged:{ color: '#94a3b8', bg: '#f1f5f9', label: 'same',    border: 'transparent' },
};

interface ScheduleCardProps {
  schedule: number[];
  otherSchedule: number[];
  isA: boolean;
  startHour: number;
  operationMode: string | undefined;
}

function ScheduleCard({ schedule, otherSchedule, isA, startHour, operationMode }: ScheduleCardProps) {
  const segs = mergeSegments(schedule);
  const segsOther = mergeSegments(otherSchedule);

  return (
    <div style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', minWidth: 0 }}>
      {segs.length === 0
        ? (
          <div style={{ padding: '12px', fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
            Not assigned
          </div>
        )
        : segs.map((seg, i) => {
          const label = seg.value < 0 ? 'Break' : formatCounterLabel(seg.value, operationMode);
          const nextSeg = segs[i + 1];
          const gapMins = nextSeg ? (nextSeg.startSlot - seg.endSlot) * 15 : 0;
          const hasBreak = gapMins > 0;

          if (isA) {
            // Left card: plain view with no change annotations
            return (
              <div key={i}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '5px 10px 5px 9px',
                  borderBottom: hasBreak ? 'none' : '1px solid #f0f4f8',
                  background: i % 2 === 0 ? 'white' : '#fafafa',
                }}>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, fontFamily: 'monospace', fontSize: 11, whiteSpace: 'nowrap' }}>
                    <span style={{ color: '#374151' }}>{slotToTime(seg.startSlot, startHour)}</span>
                    <span style={{ color: '#374151' }}>–</span>
                    <span style={{ color: '#374151' }}>{slotToTime(seg.endSlot, startHour)}</span>
                  </div>
                  <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, flexShrink: 0, minWidth: 32, textAlign: 'right' as const, color: '#1e293b' }}>
                    {label}
                  </span>
                </div>
                {hasBreak && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '2px 0', background: 'white', borderBottom: '1px solid #f0f4f8' }}>
                    <BreakDurationBadge minutes={gapMins} />
                  </div>
                )}
              </div>
            );
          }

          // Right card (B): badges for new/changed rows, inline emphasis on what changed
          const badge = segBadgeForB(seg, otherSchedule);
          const bs = BADGE_STYLES[badge];
          const rowBg = i % 2 === 0 ? 'white' : '#fafafa';
          let startChanged = false;
          let endChanged = false;
          let counterChanged = false;
          if (badge !== 'unchanged') {
            const aMatch = findBestMatchInA(seg, segsOther);
            if (!aMatch) {
              startChanged = true; endChanged = true; counterChanged = true;
            } else {
              startChanged = aMatch.startSlot !== seg.startSlot;
              endChanged = aMatch.endSlot !== seg.endSlot;
              counterChanged = normVal(aMatch.value) !== normVal(seg.value);
            }
          }

          return (
            <div key={i}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '5px 10px 5px 9px',
                borderBottom: hasBreak ? 'none' : '1px solid #f0f4f8',
                borderLeft: badge !== 'unchanged' ? `3px solid ${bs.border}` : '3px solid transparent',
                background: rowBg,
              }}>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, fontFamily: 'monospace', fontSize: 11, whiteSpace: 'nowrap' }}>
                  <span style={{ color: startChanged ? bs.color : '#374151', fontWeight: startChanged ? 700 : 400 }}>
                    {slotToTime(seg.startSlot, startHour)}
                  </span>
                  <span style={{ color: '#374151' }}>–</span>
                  <span style={{ color: endChanged ? bs.color : '#374151', fontWeight: endChanged ? 700 : 400 }}>
                    {slotToTime(seg.endSlot, startHour)}
                  </span>
                </div>
                <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, flexShrink: 0, minWidth: 32, textAlign: 'right' as const, color: counterChanged ? bs.color : '#1e293b' }}>
                  {label}
                </span>
                {/* Reserve badge space always so rows stay the same width */}
                <span style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: badge !== 'unchanged' ? bs.color : 'transparent',
                  background: badge !== 'unchanged' ? bs.bg : 'transparent',
                  borderRadius: 8,
                  padding: '1px 5px',
                  flexShrink: 0,
                  minWidth: 42,
                  textAlign: 'center' as const,
                  border: badge !== 'unchanged' ? `1px solid ${bs.border}` : '1px solid transparent',
                }}>
                  {badge !== 'unchanged' ? bs.label : ''}
                </span>
              </div>
              {hasBreak && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2px 0', background: 'white', borderBottom: '1px solid #f0f4f8' }}>
                  <BreakDurationBadge minutes={gapMins} />
                </div>
              )}
            </div>
          );
        })
      }
    </div>
  );
}

export function CompareVersionsModal({ history, officerMetadata, shiftType, operationMode, onClose, onDiffTraces }: Props) {
  const [versionA, setVersionA] = useState(() => Math.max(0, history.length - 2));
  const [versionB, setVersionB] = useState(() => history.length - 1);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const startHour = getStartHour(shiftType ?? 'morning');
  const [snapA, snapB] = useVersions([history[versionA]?.vid, history[versionB]?.vid]);
  const loading = versionA !== versionB && (!snapA || !snapB);

  // Push the grid highlight for whichever pair is selected, live — and clear it
  // the moment the pair changes or this modal goes away, so a stale trail never
  // lingers on the grid after the user picks a different comparison or closes.
  useEffect(() => {
    if (!onDiffTraces) return;
    if (versionA === versionB || !snapA?.counter_matrix || !snapB?.counter_matrix) {
      onDiffTraces(null);
      return;
    }
    onDiffTraces(computeMatrixDiffTraces(snapA.counter_matrix, snapB.counter_matrix));
    return () => onDiffTraces(null);
  }, [versionA, versionB, snapA, snapB, onDiffTraces]);

  const changedOfficerKeys = useMemo(() => {
    if (!snapA || !snapB) return [];
    const keys = new Set([
      ...Object.keys(snapA.officer_schedules),
      ...Object.keys(snapB.officer_schedules),
    ]);
    const changed = [...keys].filter(k => {
      const a = snapA.officer_schedules[k] ?? [];
      const b = snapB.officer_schedules[k] ?? [];
      return hasChanges(a, b);
    });
    return sortOfficerKeys(changed);
  }, [snapA, snapB]);

  function toggleExpand(key: string) {
    setExpandedKeys((prev: Set<string>) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const isMobile = window.innerWidth < 768;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 4600,
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        padding: isMobile ? 0 : 16,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'white',
        borderRadius: isMobile ? '12px 12px 0 0' : 10,
        width: isMobile ? '100%' : `min(${+(90 / UI_SCALE).toFixed(2)}vw, 860px)`,
        zoom: UI_SCALE,
        maxHeight: isMobile ? popupMaxH(92) : popupMaxH(84),
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        {/* Header */}
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>⇄ Compare Versions</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888', lineHeight: 1, padding: '0 4px' }}>✕</button>
        </div>

        {/* Version selectors */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f4f8', display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <select
            value={versionA}
            onChange={e => setVersionA(Number(e.target.value))}
            style={{ flex: 1, minWidth: 0, fontSize: 12, padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#f8fafc', color: '#1e293b', outline: 'none' }}
          >
            {history.map((snap, idx) => {
              const desc = snap.description.split('\n')[0];
              return (
                <option key={idx} value={idx}>
                  {`v${idx + 1} · ${snap.timestamp} · ${desc.length > 35 ? desc.slice(0, 35) + '…' : desc}`}
                </option>
              );
            })}
          </select>
          <span style={{ color: '#64748b', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>vs</span>
          <select
            value={versionB}
            onChange={e => setVersionB(Number(e.target.value))}
            style={{ flex: 1, minWidth: 0, fontSize: 12, padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#f8fafc', color: '#1e293b', outline: 'none' }}
          >
            {history.map((snap, idx) => {
              const desc = snap.description.split('\n')[0];
              return (
                <option key={idx} value={idx}>
                  {`v${idx + 1} · ${snap.timestamp} · ${desc.length > 35 ? desc.slice(0, 35) + '…' : desc}`}
                </option>
              );
            })}
          </select>
        </div>

        {/* Summary bar */}
        <div style={{ padding: '7px 16px', background: changedOfficerKeys.length > 0 ? '#fffbeb' : '#f0fdf4', borderBottom: '1px solid #f0f4f8', flexShrink: 0 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: changedOfficerKeys.length > 0 ? '#92400e' : '#166534' }}>
            {versionA === versionB
              ? 'Select two different versions to compare'
              : loading
                ? 'Loading versions…'
              : changedOfficerKeys.length === 0
                ? 'No differences found between these versions'
                : `${changedOfficerKeys.length} officer${changedOfficerKeys.length > 1 ? 's' : ''} changed`}
          </span>
        </div>

        {/* Officer list — minHeight: 0 is load-bearing: without it this flex child
            refuses to shrink below its expanded content's full height (the default
            flex min-height is "auto", i.e. content size), so expanding enough rows
            grows the whole modal past its maxHeight instead of scrolling internally,
            and the header/close button above scrolls off the top of the screen. */}
        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {/* Sticky version column headers — shown once above all officers */}
          {versionA !== versionB && snapA && snapB && changedOfficerKeys.length > 0 && (
            <div style={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              display: 'flex',
              gap: 10,
              padding: '6px 16px',
              background: '#f0f4f8',
              borderBottom: '1px solid #e2e8f0',
            }}>
              <div style={{ flex: 1, fontSize: 11, fontWeight: 700, color: '#475569', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                v{versionA + 1} · {snapA.timestamp} · {snapA.description.split('\n')[0].slice(0, 30)}
              </div>
              <div style={{ flex: 1, fontSize: 11, fontWeight: 700, color: '#475569', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                v{versionB + 1} · {snapB.timestamp} · {snapB.description.split('\n')[0].slice(0, 30)}
              </div>
            </div>
          )}

          {changedOfficerKeys.map(key => {
            const meta = officerMetadata[key];
            const name = meta?.name ?? '';
            const schedA = snapA?.officer_schedules[key] ?? [];
            const schedB = snapB?.officer_schedules[key] ?? [];
            const isExpanded = expandedKeys.has(key);

            const aHasData = schedA.some(v => v !== 0);
            const bHasData = schedB.some(v => v !== 0);
            const changeType = !aHasData && bHasData ? 'added' as const
                             : aHasData && !bHasData ? 'deleted' as const
                             : 'changed' as const;
            const changePill = changeType === 'added'
              ? { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', label: 'added' }
              : changeType === 'deleted'
              ? { bg: '#fff1f2', color: '#dc2626', border: '#fecaca', label: 'deleted' }
              : { bg: '#fffbeb', color: '#d97706', border: '#fde68a', label: 'changed' };

            return (
              <div key={key} style={{ borderBottom: '1px solid #e2e8f0' }}>
                {/* Officer header row — click to expand */}
                <div
                  onClick={() => toggleExpand(key)}
                  style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', background: isExpanded ? '#f8fafc' : 'white', userSelect: 'none' as const }}
                >
                  <span style={{ fontSize: 10, color: '#94a3b8', flexShrink: 0 }}>{isExpanded ? '▼' : '▶'}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {key}{name ? ` · ${name}` : ''}
                  </span>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 6, background: changePill.bg, color: changePill.color, border: `1px solid ${changePill.border}`, flexShrink: 0 }}>
                    {changePill.label}
                  </span>
                </div>

                {/* Expanded: dual schedule cards side by side */}
                {isExpanded && (
                  <div style={{
                    padding: '12px 16px',
                    background: '#f8fafc',
                    borderTop: '1px solid #e2e8f0',
                    display: 'flex',
                    gap: 10,
                    flexWrap: isMobile ? 'wrap' : 'nowrap' as const,
                  }}>
                    <ScheduleCard
                      schedule={schedA}
                      otherSchedule={schedB}
                      isA={true}
                      startHour={startHour}
                      operationMode={operationMode}
                    />
                    <ScheduleCard
                      schedule={schedB}
                      otherSchedule={schedA}
                      isA={false}
                      startHour={startHour}
                      operationMode={operationMode}
                    />
                  </div>
                )}
              </div>
            );
          })}

          {versionA === versionB && (
            <div style={{ padding: '32px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              Select two different versions above to see what changed.
            </div>
          )}

          {versionA !== versionB && changedOfficerKeys.length === 0 && (
            <div style={{ padding: '32px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              These two versions have identical officer schedules.
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
